/**
 * useSwipeInteraction Hook
 *
 * Adds swipe gesture support to mfersbeheaded game.
 * Detects which tile the user started touching, tracks swipe direction,
 * and triggers the appropriate swap action.
 *
 * Works alongside existing click handlers - both input methods coexist.
 */

import React, { useCallback, useRef } from 'react';

type Direction = 'up' | 'down' | 'left' | 'right';

interface SwipeState {
  startX: number;
  startY: number;
  startTime: number;
  startRow: number;
  startCol: number;
}

interface UseSwipeInteractionParams {
  boardSize: number;
  enabled: boolean;
  onSwap: (fromRow: number, fromCol: number, toRow: number, toCol: number) => void;
}

interface UseSwipeInteractionReturn {
  containerRef: React.RefObject<HTMLDivElement | null>;
  handlers: {
    onTouchStart: (e: React.TouchEvent) => void;
    onTouchEnd: (e: React.TouchEvent) => void;
    onMouseDown: (e: React.MouseEvent) => void;
    onMouseUp: (e: React.MouseEvent) => void;
  };
}

// Minimum distance in pixels to register as a swipe (not a tap)
const MIN_SWIPE_DISTANCE = 30;
// Maximum time in ms for a swipe gesture
const MAX_SWIPE_TIME = 500;

/**
 * Get the tile coordinates from a DOM element
 * Looks for data-row and data-col attributes on the element or its parents
 */
function getTileFromElement(element: Element | null): { row: number; col: number } | null {
  let current = element;

  while (current && current !== document.body) {
    const row = current.getAttribute('data-row');
    const col = current.getAttribute('data-col');

    if (row !== null && col !== null) {
      return {
        row: parseInt(row, 10),
        col: parseInt(col, 10)
      };
    }

    current = current.parentElement;
  }

  return null;
}

/**
 * Calculate the direction from dx/dy deltas
 */
function getDirection(dx: number, dy: number): Direction | null {
  const absDx = Math.abs(dx);
  const absDy = Math.abs(dy);

  // Must meet minimum distance
  if (absDx < MIN_SWIPE_DISTANCE && absDy < MIN_SWIPE_DISTANCE) {
    return null;
  }

  // Determine direction based on dominant axis
  if (absDx > absDy) {
    return dx > 0 ? 'right' : 'left';
  } else {
    return dy > 0 ? 'down' : 'up';
  }
}

/**
 * Calculate target tile based on direction
 */
function getTargetTile(
  row: number,
  col: number,
  direction: Direction,
  boardSize: number
): { row: number; col: number } | null {
  let targetRow = row;
  let targetCol = col;

  switch (direction) {
    case 'up':
      targetRow = row - 1;
      break;
    case 'down':
      targetRow = row + 1;
      break;
    case 'left':
      targetCol = col - 1;
      break;
    case 'right':
      targetCol = col + 1;
      break;
  }

  // Check bounds
  if (targetRow < 0 || targetRow >= boardSize || targetCol < 0 || targetCol >= boardSize) {
    return null;
  }

  return { row: targetRow, col: targetCol };
}

export function useSwipeInteraction({
  boardSize,
  enabled,
  onSwap
}: UseSwipeInteractionParams): UseSwipeInteractionReturn {
  const swipeState = useRef<SwipeState | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleStart = useCallback((clientX: number, clientY: number, target: EventTarget | null) => {
    if (!enabled) return;

    // Find which tile was touched
    const tile = getTileFromElement(target as Element);
    if (!tile) return;

    swipeState.current = {
      startX: clientX,
      startY: clientY,
      startTime: Date.now(),
      startRow: tile.row,
      startCol: tile.col
    };
  }, [enabled]);

  const handleEnd = useCallback((clientX: number, clientY: number) => {
    if (!enabled || !swipeState.current) return;

    const { startX, startY, startTime, startRow, startCol } = swipeState.current;
    swipeState.current = null;

    const dx = clientX - startX;
    const dy = clientY - startY;
    const dt = Date.now() - startTime;

    // Must be a quick gesture
    if (dt > MAX_SWIPE_TIME) return;

    // Get swipe direction
    const direction = getDirection(dx, dy);
    if (!direction) return;

    // Calculate target tile
    const target = getTargetTile(startRow, startCol, direction, boardSize);
    if (!target) return;

    // Trigger the swap
    onSwap(startRow, startCol, target.row, target.col);
  }, [enabled, boardSize, onSwap]);

  // Touch handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    handleStart(touch.clientX, touch.clientY, e.target);
  }, [handleStart]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    const touch = e.changedTouches[0];
    handleEnd(touch.clientX, touch.clientY);
  }, [handleEnd]);

  // Mouse handlers (for desktop testing of swipe gesture)
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Only handle left mouse button
    if (e.button !== 0) return;
    handleStart(e.clientX, e.clientY, e.target);
  }, [handleStart]);

  const handleMouseUp = useCallback((e: React.MouseEvent) => {
    handleEnd(e.clientX, e.clientY);
  }, [handleEnd]);

  return {
    containerRef,
    handlers: {
      onTouchStart: handleTouchStart,
      onTouchEnd: handleTouchEnd,
      onMouseDown: handleMouseDown,
      onMouseUp: handleMouseUp
    }
  };
}

export default useSwipeInteraction;
